import type { MouseEvent, JSX } from 'react'

import { ExternalLinkIcon, IconButton, LinkIcon } from '@buffer-mono/popcorn'

import { useMentionData } from '../Mentions/context/MentionContext'
import { copyMentionLinkToClipboard } from './copyMentionLink'

import styles from './MentionHoverActions.module.css'

/** The mention row is a click-to-expand target; these actions handle themselves. */
const stopExpansion = (event: MouseEvent): void => {
  event.stopPropagation()
}

type MentionHoverActionsProps = {
  /**
   * Whether to offer the in-app "copy link" action. Only the root mention gets
   * it: the shareable link reopens the mention-details dialog, which the
   * backend can only rebuild from a root mention id (`getMention` rejects
   * non-root mentions). Context/ancestor and reply rows pass `false` and keep
   * just the open-on-network action, whose URL is the item's own permalink.
   */
  showCopyLink?: boolean
}

/**
 * Mention actions in the top-right of the header, revealed on hover/focus:
 * open the mention on the source network, and (root only) copy its in-app
 * link. Reads the mention id from context, so the same component serves the
 * root mention, its ancestor rows, and its reply rows. Shown on collapsed and
 * expanded cards alike; the hover reveal lives in the card's CSS.
 */
export function MentionHoverActions({
  showCopyLink = true,
}: MentionHoverActionsProps): JSX.Element | null {
  const mention = useMentionData()

  // The mention's link on the source network. Absent for some
  // networks/mentions — the action hides rather than opening a dead tab.
  const externalUrl = mention.publicUrl ?? null

  // Nothing to show once the open action has no URL and copy is suppressed.
  if (!externalUrl && !showCopyLink) {
    return null
  }

  return (
    <div
      className={styles.actions}
      data-mention-actions
      data-engagement-hover-actions
    >
      {externalUrl && (
        <IconButton
          as="a"
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          label="Open in new tab"
          tooltip="Open in new tab"
          variant="tertiary"
          size="small"
          onClick={stopExpansion}
        >
          <ExternalLinkIcon stroke={2.5} />
        </IconButton>
      )}
      {showCopyLink && (
        <IconButton
          type="button"
          label="Copy link"
          tooltip="Copy link"
          variant="tertiary"
          size="small"
          onClick={(event): void => {
            stopExpansion(event)
            void copyMentionLinkToClipboard(mention.id)
          }}
        >
          <LinkIcon stroke={2.5} />
        </IconButton>
      )}
    </div>
  )
}
